import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'

import { useSystemStore } from '@/client/store/useSystemStore'
import {
  DEFAULT_PAGE_SIZE,
  PostTagsBulkAssignBody,
  TAG_PER_POST_MAX,
  TAG_TOTAL_MAX,
  TagListResponse
} from '@/typings/twitter_benchmark_campaign_posts'

import { deleteTwBenchmarkPostTagsAssign } from '../api/deleteTwBenchmarkPostTagsAssign'
import { deleteTwBenchmarkTagById } from '../api/deleteTwBenchmarkTagById'
import { getTwBenchmarkTags } from '../api/getTwBenchmarkTags'
import { postTwBenchmarkTag } from '../api/postTwBenchmarkTag'
import { postTwBenchmarkTagsBulkAssign } from '../api/postTwBenchmarkTagsBulkAssign'
import { putTwBenchmarkTagById } from '../api/putTwBenchmarkTagById'
import { TwBenchmarkApiError } from '../api/twBenchmarkApiError'
import { useNotify } from '../hooks/useNotify'
import { useTwBenchmarkPostsStore } from './useTwBenchmarkPostsStore'

type Tag = TagListResponse['tags'][number]
type Assignment = PostTagsBulkAssignBody['assignments'][number]

const TAG_NAME_MIN = 1
const TAG_NAME_MAX = 20

const FETCH_LIMIT = TAG_TOTAL_MAX

const INVALID_NAME_MESSAGE = 'タグ名は1〜20文字で入力してください。'
const TOTAL_LIMIT_MESSAGE = 'タグは500件までです。'
const PER_POST_LIMIT_MESSAGE = `タグを${TAG_PER_POST_MAX}個以下に設定してください。`
const DUPLICATE_NAME_MESSAGE = 'タグがすでに存在するため、他のタグ名を入力してください。'

const CREATE_SUCCESS_MESSAGE = 'タグを作成しました。'
const RENAME_SUCCESS_MESSAGE = 'タグ名を変更しました。'
const DELETE_SUCCESS_MESSAGE = 'タグを削除しました。'
const ASSIGN_SUCCESS_MESSAGE = 'タグを更新しました。'
const UNASSIGN_SUCCESS_MESSAGE = 'タグを解除しました。'

const DUPLICATE_ERROR_TYPE = 'DUPLICATE'

/**
 * タグ名の妥当性を検証関数
 */
function validateTagName(name: string): string | null {
  const trimmed = name.trim()
  if (trimmed.length < TAG_NAME_MIN || trimmed.length > TAG_NAME_MAX) {
    return null
  }

  return trimmed
}

/**
 * X 競合タグ管理ストア。
 */
export const useTwBenchmarkTagsStore = defineStore('twBenchmarkTagsStore', () => {
  const tags = ref<Tag[]>([])
  const pagedTags = ref<Tag[]>([])
  const totalCount = ref(0)
  const loading = ref(false)

  const currentPage = ref(1)
  const pageSize = ref<number>(DEFAULT_PAGE_SIZE)

  const systemStore = useSystemStore()
  const postsStore = useTwBenchmarkPostsStore()
  const { handleCustomErrorNotify, handleSuccessNotify } = useNotify()

  /**
   * タグ名重複（BE の DUPLICATE）のみ専用文言でトーストを出す（他のエラーは既存の汎用通知に任せる）。
   */
  const notifyIfDuplicate = (error?: TwBenchmarkApiError): void => {
    if (error?.type !== DUPLICATE_ERROR_TYPE) return

    handleCustomErrorNotify(DUPLICATE_NAME_MESSAGE)
  }

  /**
   * 変更系成功後の同期（全件・現在ページ・投稿一覧をまとめて再取得する）。
   */
  const syncAfterMutation = async (): Promise<void> => {
    await fetchTags()
    await fetchPagedTags()
    await postsStore.refresh()
  }

  const fetchTags = async (): Promise<void> => {
    const groupId = systemStore.currentGroupId
    if (typeof groupId !== 'number') return

    loading.value = true

    try {
      const result = await getTwBenchmarkTags({
        groupId,
        sortBy: 'id',
        order: 'asc',
        limit: FETCH_LIMIT,
        offset: 0
      })

      if (result.isSuccess && result.data) {
        tags.value = result.data.tags
        totalCount.value = result.data.totalCount
      }
    } finally {
      loading.value = false
    }
  }

  /**
   * 管理ダイアログ用に現在ページのタグを limit/offset で取得する（引数省略時は保持中の条件で表示位置を保つ）。
   */
  const fetchPagedTags = async (page?: number, size?: number): Promise<void> => {
    const groupId = systemStore.currentGroupId
    if (typeof groupId !== 'number') return

    if (typeof size === 'number') pageSize.value = size
    if (typeof page === 'number') currentPage.value = page

    loading.value = true

    try {
      const fetchPage = async (targetPage: number): Promise<number | null> => {
        const result = await getTwBenchmarkTags({
          groupId,
          sortBy: 'id',
          order: 'asc',
          limit: pageSize.value,
          offset: (targetPage - 1) * pageSize.value
        })

        if (!result.isSuccess || !result.data) return null

        pagedTags.value = result.data.tags
        totalCount.value = result.data.totalCount

        return result.data.totalCount
      }

      const fetchedTotal = await fetchPage(currentPage.value)
      if (fetchedTotal === null) return

      const lastPage = Math.max(1, Math.ceil(fetchedTotal / pageSize.value))
      if (currentPage.value > lastPage) {
        currentPage.value = lastPage
        await fetchPage(lastPage)
      }
    } finally {
      loading.value = false
    }
  }

  const createTag = async (name: string): Promise<boolean> => {
    const groupId = systemStore.currentGroupId
    if (typeof groupId !== 'number') return false

    const trimmed = validateTagName(name)
    if (trimmed === null) {
      handleCustomErrorNotify(INVALID_NAME_MESSAGE)

      return false
    }

    if (tags.value.length >= TAG_TOTAL_MAX) {
      handleCustomErrorNotify(TOTAL_LIMIT_MESSAGE)

      return false
    }

    const result = await postTwBenchmarkTag(groupId, { name: trimmed })

    if (!result.isSuccess) {
      notifyIfDuplicate(result.error)

      return false
    }

    await syncAfterMutation()
    handleSuccessNotify(CREATE_SUCCESS_MESSAGE)

    return true
  }

  const renameTag = async (tagId: number, name: string): Promise<boolean> => {
    const groupId = systemStore.currentGroupId
    if (typeof groupId !== 'number') return false

    const trimmed = validateTagName(name)
    if (trimmed === null) {
      handleCustomErrorNotify(INVALID_NAME_MESSAGE)

      return false
    }

    const result = await putTwBenchmarkTagById(tagId, groupId, { name: trimmed })

    if (!result.isSuccess) {
      notifyIfDuplicate(result.error)

      return false
    }

    await syncAfterMutation()
    handleSuccessNotify(RENAME_SUCCESS_MESSAGE)

    return true
  }

  const deleteTag = async (tagId: number): Promise<number | null> => {
    const groupId = systemStore.currentGroupId
    if (typeof groupId !== 'number') return null

    const result = await deleteTwBenchmarkTagById(tagId, groupId)
    if (!result.isSuccess || !result.data) return null

    await syncAfterMutation()
    handleSuccessNotify(DELETE_SUCCESS_MESSAGE)

    return result.data.releasedCount
  }

  const overwritePostTags = async (postId: number, tagIds: number[]): Promise<boolean> => {
    return bulkAssignTags([{ postId, tagIds }])
  }

  const bulkAssignTags = async (assignments: Assignment[]): Promise<boolean> => {
    const groupId = systemStore.currentGroupId
    if (typeof groupId !== 'number') return false

    const isOverPerPostLimit = assignments.some(({ tagIds }) => tagIds.length > TAG_PER_POST_MAX)
    if (isOverPerPostLimit) {
      handleCustomErrorNotify(PER_POST_LIMIT_MESSAGE)

      return false
    }

    const result = await postTwBenchmarkTagsBulkAssign(groupId, { assignments })
    if (!result.isSuccess) return false

    await syncAfterMutation()
    handleSuccessNotify(ASSIGN_SUCCESS_MESSAGE)

    return true
  }

  const unassignPostTags = async (postId: number, tagIds: number[]): Promise<boolean> => {
    const groupId = systemStore.currentGroupId
    if (typeof groupId !== 'number') return false

    const result = await deleteTwBenchmarkPostTagsAssign(postId, groupId, { tagIds })
    if (!result.isSuccess) return false

    await syncAfterMutation()
    handleSuccessNotify(UNASSIGN_SUCCESS_MESSAGE)

    return true
  }

  return {
    tags: readonly(tags),
    pagedTags: readonly(pagedTags),
    totalCount: readonly(totalCount),
    loading: readonly(loading),
    currentPage: readonly(currentPage),
    pageSize: readonly(pageSize),

    fetchTags,
    fetchPagedTags,
    createTag,
    renameTag,
    deleteTag,
    overwritePostTags,
    bulkAssignTags,
    unassignPostTags
  }
})
